import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, DatePicker, Modal, Space, Tag, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useGetScenarioTemplateQuery, useMoveSaveCursorMutation } from '../../api/savesV2Api';
import { useRefreshWorld, useSave } from '../../contexts/WorldContext';
import { toastError } from '../../utils/toast';
import { formatDate } from '../../i18n';

interface ScenarioDayControlProps {
  saveId?: string | null;
}

const ScenarioDayControl: React.FC<ScenarioDayControlProps> = ({ saveId }) => {
  const { t } = useTranslation('scenarios_admin');
  const save = useSave();
  const refreshWorld = useRefreshWorld();
  const [scenarioDateTarget, setScenarioDateTarget] = useState<Dayjs | null>(null);
  const [moveSaveCursor, { isLoading: movingScenarioCursor }] = useMoveSaveCursorMutation();

  const { data: scenarioTemplate } = useGetScenarioTemplateQuery(
    {
      templateId: save.scenarioTemplateId || '',
      version: save.scenarioTemplateVersion ?? undefined,
    },
    {
      skip: save.type !== 'scenario' || !save.scenarioTemplateId,
    },
  );

  const scenarioCursorDate = save.type === 'scenario' ? (save.cursor?.simDate || save.cursor?.sim_date || null) : null;

  const scenarioDateBounds = useMemo(() => {
    if (save.type !== 'scenario') return null;

    const startRaw = scenarioTemplate?.worldSpec?.start_date
      || scenarioTemplate?.worldSpec?.startDate
      || null;

    const startDate = startRaw && dayjs(startRaw).isValid()
      ? dayjs(startRaw).format('YYYY-MM-DD')
      : null;

    const endFromTemplate = startDate && Number.isFinite(Number(scenarioTemplate?.durationDays))
      ? dayjs(startDate).add(Number(scenarioTemplate?.durationDays), 'day').format('YYYY-MM-DD')
      : null;

    const endRaw = endFromTemplate || null;
    const endDate = endRaw && dayjs(endRaw).isValid()
      ? dayjs(endRaw).format('YYYY-MM-DD')
      : null;

    if (!startDate && !endDate) return null;
    return { startDate, endDate };
  }, [save.type, scenarioTemplate]);

  const scenarioCursorAtStart = useMemo(() => {
    if (!scenarioCursorDate || !scenarioDateBounds?.startDate) return false;
    return dayjs(scenarioCursorDate).isSame(dayjs(scenarioDateBounds.startDate), 'day');
  }, [scenarioCursorDate, scenarioDateBounds?.startDate]);

  const scenarioCursorAtEnd = useMemo(() => {
    if (!scenarioCursorDate || !scenarioDateBounds?.endDate) return false;
    return dayjs(scenarioCursorDate).isSame(dayjs(scenarioDateBounds.endDate), 'day');
  }, [scenarioCursorDate, scenarioDateBounds?.endDate]);

  useEffect(() => {
    if (save.type !== 'scenario') return;
    setScenarioDateTarget(scenarioCursorDate ? dayjs(scenarioCursorDate) : null);
  }, [save.type, scenarioCursorDate]);

  const isScenarioDateDisabled = (date: Dayjs) => {
    if (!scenarioDateBounds) return false;
    if (scenarioDateBounds.startDate && date.isBefore(dayjs(scenarioDateBounds.startDate), 'day')) {
      return true;
    }
    if (scenarioDateBounds.endDate && date.isAfter(dayjs(scenarioDateBounds.endDate), 'day')) {
      return true;
    }
    return false;
  };

  const openDeepRewindConfirm = (targetDate: string, estimate: {
    ticks_to_recalculate?: number;
    events_after_cursor?: number;
    invalidated_events_estimate?: number;
    estimated_replay_cost_ms?: number;
  }) => {
    Modal.confirm({
      title: t('dayControl.confirm.title', { date: formatDate(targetDate, 'short') }),
      content: (
        <Space direction="vertical" size={4}>
          <strong>{t('common:messages.rewindRemoves', { date: formatDate(targetDate, 'short') })}</strong>
          <span>{t('dayControl.confirm.replayRequired')}</span>
          <span>{t('dayControl.confirm.ticks', { value: String(estimate.ticks_to_recalculate ?? 0) })}</span>
          <span>{t('dayControl.confirm.eventsAfterCursor', { value: String(estimate.events_after_cursor ?? 0) })}</span>
          <span>{t('dayControl.confirm.invalidated', { value: String(estimate.invalidated_events_estimate ?? 0) })}</span>
          <span>{t('dayControl.confirm.replayCost', { ms: String(estimate.estimated_replay_cost_ms ?? 0) })}</span>
        </Space>
      ),
      okText: t('dayControl.confirm.ok'),
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        await applyScenarioCursorMove(targetDate, true);
      },
    });
  };

  const applyScenarioCursorMove = async (targetDate: string, confirmDeep = false) => {
    if (!saveId) return;

    try {
      const result = await moveSaveCursor({
        saveId,
        body: {
          sim_date: targetDate,
          expected_save_revision: save.revision,
          ...(confirmDeep ? { confirm_deep_rewind: true } : {}),
        },
      }).unwrap();

      if ('status' in result && result.status === 'requires_confirmation') {
        openDeepRewindConfirm(targetDate, result.estimate);
        return;
      }

      await refreshWorld();
      message.success(t('dayControl.moved', { date: formatDate(targetDate, 'short') }));
    } catch (error) {
      const err = error as {
        data?: {
          message?: string;
          estimate?: {
            ticks_to_recalculate?: number;
            events_after_cursor?: number;
            invalidated_events_estimate?: number;
            estimated_replay_cost_ms?: number;
          };
          reason?: string;
        };
      };

      if (err.data?.estimate && err.data.reason === 'deep_rewind') {
        openDeepRewindConfirm(targetDate, err.data.estimate);
        return;
      }

      toastError(err.data?.message || error);
    }
  };

  const handleScenarioStepDay = (deltaDays: number) => {
    if (!scenarioCursorDate) return;
    const nextDate = dayjs(scenarioCursorDate).add(deltaDays, 'day');
    if (isScenarioDateDisabled(nextDate)) {
      return;
    }
    void applyScenarioCursorMove(nextDate.format('YYYY-MM-DD'));
  };

  const handleScenarioJumpToDate = () => {
    if (!scenarioDateTarget) {
      toastError(t('dayControl.errors.pickDate'));
      return;
    }
    if (isScenarioDateDisabled(scenarioDateTarget)) {
      toastError(t('dayControl.errors.outOfWindow'));
      return;
    }
    void applyScenarioCursorMove(scenarioDateTarget.format('YYYY-MM-DD'));
  };

  if (save.type !== 'scenario' || !saveId) {
    return null;
  }

  return (
    <Card size="small">
      <Space wrap>
        <Tag color="gold">
          {t('dayControl.currentDay', { date: scenarioCursorDate ? formatDate(scenarioCursorDate, 'short') : '—' })}
        </Tag>
        {scenarioDateBounds?.startDate && scenarioDateBounds?.endDate ? (
          <Tag color="blue">
            {t('dayControl.window', {
              start: formatDate(scenarioDateBounds.startDate, 'dayMonthShort'),
              end: formatDate(scenarioDateBounds.endDate, 'dayMonthShort'),
            })}
          </Tag>
        ) : null}
        <Button
          onClick={() => handleScenarioStepDay(-1)}
          disabled={!scenarioCursorDate || scenarioCursorAtStart}
          loading={movingScenarioCursor}
        >
          {t('dayControl.prevDay')}
        </Button>
        <Button
          onClick={() => handleScenarioStepDay(1)}
          disabled={!scenarioCursorDate || scenarioCursorAtEnd}
          loading={movingScenarioCursor}
        >
          {t('dayControl.nextDay')}
        </Button>
        <DatePicker
          value={scenarioDateTarget}
          onChange={setScenarioDateTarget}
          disabledDate={isScenarioDateDisabled}
          allowClear={false}
        />
        <Button
          type="primary"
          onClick={handleScenarioJumpToDate}
          disabled={!scenarioDateTarget || scenarioDateTarget.format('YYYY-MM-DD') === scenarioCursorDate}
          loading={movingScenarioCursor}
        >
          {t('dayControl.jump')}
        </Button>
      </Space>
    </Card>
  );
};

export default ScenarioDayControl;
